import React, { createContext, useContext, useEffect, useState } from "react";
import { Route, Switch, useLocation } from "react-router-dom";
import {
  InitializeGA,
  SetDefaultConsentMode,
} from "../utilities/GoogleAnalyticsEvents";
import Navbar from "../../src/components/Navbar/FRNavbar/Navbar";
import ContactUs from "../../src/components/Pages/FRPages/ContactUs/ContactUs";
import PrivacyPolicy from "../../src/components/Pages/FRPages/PrivacyPolicy/PrivacyPolicy";
import Terms from "../../src/components/Pages/FRPages/Terms/Terms";
import Home from "../../src/components/Pages/FRPages/Home/Home";
import ClinicalStudies from "../../src/components/Pages/FRPages/HealthcareProviders/ClinicalStudies/ClinicalStudies";
import Events from "../../src/components/Pages/FRPages/HealthcareProviders/Events/Events";
import WhyVnotes from "../components/Pages/FRPages/WhyVnotes/WhyVnotes";
import PatientResources from "../components/Pages/FRPages/Patients/PatientResources";
import PatientTestimonials from "../../src/components/Pages/FRPages/Testimonials/Patients/Patient-testimonials";
import HCPTestimonials from "../../src/components/Pages/FRPages/Testimonials/HCP/HCP-testimonials";
import RedirectPage from "../../src/components/Pages/FRPages/Redirect/Redirect";
import NotFound from "../../src/components/Pages/FRPages/NotFound/NotFound";
import FRFooter from "../components/Footer/FRFooter/FRFooter";
import { generateHreflangTags } from "../utilities/generateHreflangTags";
import { ScrollDepthTracker } from "../utilities/ScrollDepthTracker";
import ScrollToTop from "../components/ScrollToTop/ScrollToTop";
import SEO from "../components/SEO";
import HCPModal from "../components/Modals/HCP/FRHCPModal/FRHCPModal";
import CookieBanner from "../components/Modals/DataConsent/CookieBannerFR/CookieBannerFR";
import PreloadBanners from "../components/PreloadBanners/PreloadBanners";
import { Helmet } from "react-helmet-async";
import RegionContext from "../RegionContext";

export const ModalContext = createContext();

const FRLayout = () => {
  const region = useContext(RegionContext);
  const [isHCPModalVisible, setIsHCPModalVisible] = useState(false);
  const [isCookieBannerVisible, setIsCookieBannerVisible] = useState(false);
  const location = useLocation();

  useEffect(() => {
    SetDefaultConsentMode();
    InitializeGA(region);
  }, [region]);

  const seoData = {
    "/fr/": {
      title: "Home - vNOTES fr",
      description:
        "Welcome to vNOTES - the next generation in minimally invasive gynecologic surgery.",
      keywords: "vnotes, minimally invasive surgery, gynecologic surgery",
      image:
        "https://amccorpwebappsstorageprd.blob.core.windows.net/appliedmedicalus/IMG/vNOTES_OG.png",
      url: `https://www.vnotes.com/fr/`,
    },
    "/fr/clinicalevidence": {
      title: "Clinical Evidence - vNOTES fr",
      description:
        "Explore clinical studies on vNOTES surgery to understand its efficacy and safety.",
      keywords:
        "vnotes clinical evidence, vnotes studies, gynecologic surgery research",
      image:
        "https://amccorpwebappsstorageprd.blob.core.windows.net/appliedmedicalus/IMG/vNOTES_OG.png",
      url: `https://www.vnotes.com/fr/clinicalevidence`,
    },
    "/fr/events": {
      title: "Events - vNOTES fr",
      description:
        "Join us at upcoming events to learn more about vNOTES surgical techniques and advancements.",
      keywords:
        "vnotes events, surgical conferences, gynecologic surgery events",
      image:
        "https://amccorpwebappsstorageprd.blob.core.windows.net/appliedmedicalus/IMG/vNOTES_OG.png",
      url: `https://www.vnotes.com/fr/events`,
    },
    "/fr/healthcareprofessionals": {
      title: "Healthcare Professionals - vNOTES fr",
      description:
        "Resources and training for healthcare professionals specializing in vNOTES.",
      keywords:
        "vnotes healthcare professionals, medical training, gynecologic surgery resources",
      image:
        "https://amccorpwebappsstorageprd.blob.core.windows.net/appliedmedicalus/IMG/vNOTES_OG.png",
      url: `https://www.vnotes.com/fr/healthcareprofessionals`,
    },
    "/fr/patients": {
      title: "For Patients - vNOTES fr",
      description:
        "Learn more about how vNOTES can provide a minimally invasive option for gynecologic surgery.",
      keywords: "vnotes for patients, minimally invasive gynecologic surgery",
      image:
        "https://amccorpwebappsstorageprd.blob.core.windows.net/appliedmedicalus/IMG/vNOTES_OG.png",
      url: `https://www.vnotes.com/fr/patients`,
    },
    "/fr/patientstestimonials": {
      title: "Patient Testimonials - vNOTES fr",
      description:
        "Hear from patients who have experienced the benefits of vNOTES surgery.",
      keywords: "vnotes testimonials, patient experiences, gynecologic surgery",
      image:
        "https://amccorpwebappsstorageprd.blob.core.windows.net/appliedmedicalus/IMG/vNOTES_OG.png",
      url: `https://www.vnotes.com/fr/patientstestimonials`,
    },
    "/fr/hcptestimonials": {
      title: "HCP Testimonials - vNOTES fr",
      description:
        "Testimonials from healthcare professionals on the efficacy of vNOTES.",
      keywords:
        "vnotes hcp testimonials, professional reviews, gynecologic surgery",
      image:
        "https://amccorpwebappsstorageprd.blob.core.windows.net/appliedmedicalus/IMG/vNOTES_OG.png",
      url: `https://www.vnotes.com/fr/hcptestimonials`,
    },
    "/fr/findasurgeon": {
      title: "Find a Surgeon - vNOTES fr",
      description:
        "Locate a certified surgeon trained in the vNOTES technique near you.",
      keywords: "find a vnotes surgeon, gynecologic surgery",
      image:
        "https://amccorpwebappsstorageprd.blob.core.windows.net/appliedmedicalus/IMG/vNOTES_OG.png",
      url: `https://www.vnotes.com/fr/findasurgeon`,
    },
    "/fr/contactus": {
      title: "Contact Us - vNOTES fr",
      description:
        "Get in touch with the vNOTES team for more information and support.",
      keywords: "vnotes contact, gynecologic surgery contact",
      image:
        "https://amccorpwebappsstorageprd.blob.core.windows.net/appliedmedicalus/IMG/vNOTES_OG.png",
      url: `https://www.vnotes.com/fr/contactus`,
    },
  };

  // Retrieve metadata based on the current path
  const currentSEO = seoData[location.pathname] || {
    title: "vNOTES fr – Advanced Minimally Invasive Gynecologic Surgery",
    description:
      "vNOTES (vaginal natural orifice transluminal endoscopic surgery) modernizes vaginal surgery.",
    keywords: "vnotes, surgery, gynecology",
    image:
      "https://amccorpwebappsstorageprd.blob.core.windows.net/appliedmedicalus/IMG/vNOTES_OG.png",
    url: `https://www.vnotes.com/fr${location.pathname}`,
  };

  // Generate hreflang tags for the current route and region
  const hreflangTags = generateHreflangTags(location.pathname, region) || [];

  return (
    <ModalContext.Provider
      value={[
        isHCPModalVisible,
        setIsHCPModalVisible,
        isCookieBannerVisible,
        setIsCookieBannerVisible,
      ]}
    >
      <SEO
        title={currentSEO.title}
        description={currentSEO.description}
        keywords={currentSEO.keywords}
        image={currentSEO.image}
        url={currentSEO.url}
      />

      <Helmet>
        {hreflangTags.map((tag) => (
          <link
            key={tag.lang}
            rel="alternate"
            href={tag.url}
            hrefLang={tag.lang}
          />
        ))}
      </Helmet>

      <Navbar />
      <ScrollDepthTracker />
      <HCPModal />
      <ScrollToTop />
      <PreloadBanners />

      <Switch>
        <Route path="/fr/" exact component={Home} />
        <Route path="/fr/clinicalevidence" exact component={ClinicalStudies} />
        <Route path="/fr/clinicalevidence/:ref" component={ClinicalStudies} />
        <Route path="/fr/events" exact component={Events} />
        <Route path="/fr/WhyVnotes" exact component={WhyVnotes} />
        <Route path="/fr/WhyVnotes/:ref" component={WhyVnotes} />
        <Route path="/fr/patientresources" exact component={PatientResources} />
        <Route
          path="/fr/patientstestimonials"
          exact
          component={PatientTestimonials}
        />
        <Route path="/fr/hcptestimonials" exact component={HCPTestimonials} />
        <Route path="/fr/contactus" exact component={ContactUs} />
        <Route path="/fr/redirect" exact component={RedirectPage} />
        <Route path="/fr/privacypolicy" exact component={PrivacyPolicy} />
        <Route path="/fr/legalnotice" exact component={Terms} />
        <Route path="*" component={NotFound} />
      </Switch>
      <FRFooter />
      <CookieBanner />
    </ModalContext.Provider>
  );
};

export default FRLayout;
